import { useState, useEffect } from 'react';
import { FileText, Plus, CheckCircle, XCircle, Clock, Building, Home, MapPin, DollarSign } from 'lucide-react';
import { useDevisRequests } from '../../hooks/useDevisRequests';
import { useTexts } from '../../hooks/useTexts';
import Breadcrumb from '../../components/common/Breadcrumb';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import { DevisRequestForm } from '../../components/forms/DevisRequestForm';
import { DemandeDevisRequest } from '../../types/api';

export default function DevisRequestsPage() {
  const { getText } = useTexts();
  const {
    availableTypes,
    assignations,
    loading,
    error,
    fetchAvailableTypes,
    fetchMesDemandes,
    faireDemandeDevis
  } = useDevisRequests();

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchAvailableTypes();
    fetchMesDemandes();
  }, []);

  const handleSubmitRequest = async (data: DemandeDevisRequest) => {
    setSubmitting(true);
    setSuccess(null);

    try {
      await faireDemandeDevis(data);
      setSuccess('Demande de devis envoyée avec succès !');
      setShowRequestForm(false);
    } catch (err) {
      // L'erreur est gérée par le hook
    } finally {
      setSubmitting(false);
    }
  };


  const getStatusIcon = (statut: string) => {
    switch (statut) {
      case 'ACCEPTE':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'REFUSE':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'EN_ATTENTE':
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusText = (statut: string) => {
    switch (statut) {
      case 'ACCEPTE':
        return 'Accepté';
      case 'REFUSE':
        return 'Refusé';
      case 'EN_ATTENTE':
      default:
        return 'En attente d\'acceptation';
    }
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'ACCEPTE':
        return 'bg-green-100 text-green-800';
      case 'REFUSE':
        return 'bg-red-100 text-red-800';
      case 'EN_ATTENTE':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading && availableTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('devis.title'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{getText('devis.title')}</h1>
          <p className="text-gray-600 mt-2">
            {getText('devis.description')}
          </p>
        </div>

        {/* Messages d'erreur et de succès */}
        {error && (
          <div className="mb-6">
            <ErrorMessage error={error} onDismiss={() => {}} />
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Bouton pour faire une nouvelle demande */}
        <div className="mb-8">
          <button
            onClick={() => setShowRequestForm(true)}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Faire une demande de devis
          </button>
        </div>

        {/* Formulaire de demande de devis */}
        {showRequestForm && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Nouvelle demande de devis
            </h2>
            
            <DevisRequestForm
              onSubmit={handleSubmitRequest}
              onCancel={() => setShowRequestForm(false)}
              isLoading={submitting}
            />
          </div>
        )}

        {/* Liste des demandes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Mes demandes de devis
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : assignations.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucune demande de devis pour le moment</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {assignations.map((assignation) => (
                <div key={assignation.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Building className="w-5 h-5 text-gray-400 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          {assignation.devis_titre}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>Demandé le {new Date(assignation.date_assignation).toLocaleDateString('fr-FR')}</span>
                      </div>

                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Home className="w-4 h-4 mr-1" />
                          <span>{assignation.type_maison_designation} (Niveau {assignation.niveau})</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>{assignation.projet_nom}</span>
                        </div>
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          <span>{assignation.prix_unitaire_ht.toLocaleString('fr-FR')} FCFA</span>
                        </div>
                        <div className="flex items-center">
                          <Building className="w-4 h-4 mr-1" />
                          <span>{assignation.surface_totale} m²</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assignation.statut)}`}>
                        <div className="flex items-center">
                          {getStatusIcon(assignation.statut)}
                          <span className="ml-1">{getStatusText(assignation.statut)}</span>
                        </div>
                      </div>

                      {/* {assignation.statut === 'EN_ATTENTE' && (
                        <div className="text-sm text-yellow-600 font-medium">
                          ⏳ En attente de validation par l'administrateur
                        </div>
                      )} */}
                      
                      {/* {assignation.statut === 'ACCEPTE' && (
                        <div className="text-sm text-green-600 font-medium">
                          ✓ Validé par l'administrateur
                        </div>
                      )} */}
                      
                      {/* {assignation.statut === 'REFUSE' && (
                        <div className="text-sm text-red-600 font-medium">
                          ✗ Refusé par l'administrateur
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
